@if (!dialogMode()) {
	<vex-page-layout>
		<div *transloco="let t">
			<vex-page-layout-header class="pb-16 flex flex-col justify-center items-start">
				<div>
					<h1 class="title mt-0 mb-1">{{ t('realestates.realestates') }}</h1>
					<vex-breadcrumbs [crumbs]="this.breadCrumbService.crumbs"></vex-breadcrumbs>
				</div>
			</vex-page-layout-header>
			<vex-page-layout-content [class.container]="false" [class.px-gutter]="true" class="-mt-6">
				<!-- Reusable table template -->
				<ng-container *ngTemplateOutlet="realEstatesTableTemplate"></ng-container>
			</vex-page-layout-content>
		</div>
	</vex-page-layout>
} @else {
	<!-- Dialog mode: render template directly without page layout -->
	<ng-container *ngTemplateOutlet="realEstatesTableTemplate"></ng-container>
}

<!-- Reusable Table Template - can be used via ngTemplateOutlet in dialogs -->
<!-- Context: { onRowClick: (row) => void } -->
<ng-template #realEstatesTableTemplate>
	<div *transloco="let t">
		<div class="card overflow-auto" [class.-mt-16]="!dialogMode()">
			<div class="bg-app-bar px-6 h-16 border-b sticky left-0 flex flex-row justify-start items-center">
				<h2 class="title my-0 ltr:pr-4 rtl:pl-4 ltr:mr-4 rtl:ml-4 ltr:border-r rtl:border-l hidden xs:block">
					<span>{{ t('realestates.realestates') }}</span>
				</h2>
				<div class="bg-card rounded-full border px-4 bg-white flex flex-row flex-1 max-w-[800px] justify-start items-center">
					<mat-icon [svgIcon]="icons.get('search')" size="20px"></mat-icon>
					<input
						[delayedSearch]="searchFilterSubject"
						class="px-4 py-3 border-0 outline-none w-full bg-transparent"
						matInput
						name="searchField"
						placeholder="{{ t('realestates.searchPlaceholder') }}"
						type="search" />
				</div>
				<span class="flex-1"></span>
			</div>

			@if (!finishedLoading()) {
				<div class="flex justify-center items-center py-12">
					<mat-progress-spinner color="primary" mode="indeterminate"></mat-progress-spinner>
				</div>
			}

			<table (matSortChange)="sortData($event)" @stagger [dataSource]="dataSource" class="w-full" mat-table matSort name="realEstatesTable">
				<ng-container matColumnDef="selectForSmokeDetectorCheckPrint">
					<th *matHeaderCellDef mat-header-cell>
						<mat-checkbox
							(change)="$event ? masterToggle() : null"
							[checked]="selection.hasValue() && selectToggleEnabled() && allSelected()"
							[indeterminate]="selection.hasValue() && !allSelected()"></mat-checkbox>
					</th>
					<td *matCellDef="let row" mat-cell>
						<mat-checkbox
							(change)="$event ? selection.toggle(row) : null"
							(click)="$event.stopPropagation()"
							onKeyDown="$event.stopPropagation()"
							onKeyUp="$event.stopPropagation()"
							[checked]="selection.isSelected(row)"></mat-checkbox>
					</td>
				</ng-container>

				<!-- Area Column -->
				<ng-container matColumnDef="area">
					<th *matHeaderCellDef mat-header-cell mat-sort-header>
						<div class="text-start">
							{{ t('realestates.number') }}
							<br />
							{{ t('common.serviceCompany') }}
						</div>
					</th>
					<td *matCellDef="let row" [attr.data-label]="t('realestates.area')" mat-cell>
						<div class="flex flex-row items-center justify-start" [matTooltip]="getAreaTooltip(row)" matTooltipClass="multiline-tooltip">
							@if (sessionService.cachedFavIconsByArea.has(row.realEstateArea)) {
								<img [src]="getIconUrl(row.realEstateArea)" alt="Icon" width="32" height="32" />
							}
							<div class="flex flex-row ml-1">
								<span>{{ row.realEstateArea }}</span>
								<span class="mx-1">/</span>
								<span class="font-bold text-lg">{{ row.realEstateNumber }}</span>
							</div>
						</div>
					</td>
				</ng-container>

				<!-- Address Column -->
				<ng-container matColumnDef="address">
					<th *matHeaderCellDef mat-header-cell mat-sort-header>
						{{ t('user.address') }}
					</th>
					<td *matCellDef="let row" [attr.data-label]="t('user.address')" mat-cell>
						<div class="inline-flex flex-row justify-start items-center gap-2 px-3 py-1 my-1">
							<a
								[href]="'https://www.google.com/maps?q=' + row.address?.street + ', ' + row.address?.zipCode + ' ' + row.address?.city"
								target="_blank"
								(click)="$event.stopPropagation()"
								mat-icon-button
								color="primary"
								class="flex-shrink-0">
								<img src="assets/img/google-maps-icon-2020.svg" alt="Google Maps" class="w-5 h-5 object-contain" />
							</a>
							<div class="flex flex-col justify-start items-start leading-tight">
								<span class="text-sm text-black">{{ row.address?.street }}</span>
								<span class="text-xs text-gray-600">{{ row.address?.zipCode }} {{ row.address?.city }}</span>
							</div>
						</div>
					</td>
				</ng-container>

				<ng-container matColumnDef="propertyManagement">
					<th *matHeaderCellDef mat-header-cell>
						{{ t('realestates.propertyManagement') }}
					</th>
					<td *matCellDef="let row" mat-cell>
						<div class="flex flex-col justify-start items-start leading-tight">
							<span class="text-sm text-black">
								{{ row.propertyManagementCompany?.companyNames[0] }}
								{{ row.propertyManagementCompany?.companyNames[1] }}
								@if (row.propertyManagementAccountNumber) {
									<span class="text-xs text-gray-500">({{ row.propertyManagementAccountNumber }})</span>
								}
								@if (row.propertyManagementNumber?.trim()) {
									<br />
									<span class="text-xs text-gray-500">
										{{ t('realestates.realEstateNumberAsPMSubtitle', { propertyManagementNumber: row.propertyManagementNumber }) }}
									</span>
								}
							</span>
						</div>
					</td>
				</ng-container>

				<ng-container matColumnDef="lastInspectionDate">
					<th *matHeaderCellDef mat-header-cell mat-sort-header>
						{{ t('smokeDetectorCheck.lastInspectionDate') }}
					</th>
					<td *matCellDef="let row" [attr.data-label]="t('smokeDetectorCheck.lastInspectionDate')" mat-cell>
						{{ dateUtil.setDateFormatByActiveLang(row.lastInspectionDate, '') }}
					</td>
				</ng-container>

				<ng-container matColumnDef="nextInspectionDate">
					<th *matHeaderCellDef mat-header-cell mat-sort-header>
						{{ t('smokeDetectorCheck.nextInspectionDate') }}
					</th>
					<td *matCellDef="let row" [attr.data-label]="t('smokeDetectorCheck.nextInspectionDate')" mat-cell>
						{{ dateUtil.setDateFormatByActiveLang(row.nextInspectionDate, '') }}
					</td>
				</ng-container>

				<ng-container matColumnDef="inspectionState">
					<th *matHeaderCellDef mat-header-cell mat-sort-header>
						{{ t('smokeDetectorCheck.inspectionState') }}
					</th>
					<td
						*matCellDef="let row"
						[attr.data-label]="t('enum.' + row.inspectionState)"
						[matTooltip]="row.nextExecutionDate"
						[matTooltipDisabled]="row.inspectionState !== 'IN_PROGRESS'"
						mat-cell>
						<mat-icon
							[class]="iconHelper.getColorByInspectionState(row.inspectionState, row.operationalState)"
							[svgIcon]="iconHelper.getIconByInspectionState(row.inspectionState, row.operationalState)"></mat-icon>
					</td>
				</ng-container>

				<ng-container matColumnDef="operationalState">
					<th *matHeaderCellDef mat-header-cell mat-sort-header>
						{{ t('smokeDetectorCheck.operationalState') }}
					</th>
					<td *matCellDef="let row" [attr.data-label]="t('enum.' + row.orderState)" mat-cell>
						<mat-icon [svgIcon]="iconHelper.iconByCiState(row.orderState)"></mat-icon>
					</td>
				</ng-container>

				<ng-container matColumnDef="billings">
					<th *matHeaderCellDef mat-header-cell>
						{{ t('billing.billings') }}
					</th>
					<td *matCellDef="let row" mat-cell>
						@if (row.billings === undefined) {
							<mat-spinner diameter="20" class="m-2"></mat-spinner>
						} @else if (row.billings.length > 0) {
							@for (billing of row.billings; track billing.billingId) {
								<button
									type="button"
									(click)="$event.stopPropagation(); onBillingClick(row, billing)"
									class="billing-chip inline-flex flex-row justify-start items-center gap-2 border border-primary/30 rounded-2xl bg-primary/5 px-3 py-1 m-1 cursor-pointer transition-all duration-200 hover:bg-primary/20 hover:border-primary/50 hover:shadow-sm">
									<mat-icon class="text-primary flex-shrink-0">receipt_long</mat-icon>
									<div class="flex flex-col justify-start items-start leading-tight">
										<span class="text-sm text-black">{{ billing.billingNumber }}</span>
										<span class="text-xs text-gray-600">{{ billing.billingName }}</span>
									</div>
								</button>
							}
						}
					</td>
				</ng-container>

				<tr *matHeaderRowDef="visibleColumns" mat-header-row class="!hidden"></tr>
				<tr
					(click)="onRealEstate(row)"
					onKeyDown="onRealEstate(row)"
					onKeyUp="onRealEstate(row)"
					mat-row
					*matRowDef="let row; columns: visibleColumns"
					@fadeInUp
					class="hover:bg-hover trans-ease-out cursor-pointer"
					name="realEstatesRow"></tr>

				<!-- Row shown when there is no matching data. -->
				<tr *matNoDataRow class="mat-row">
					<td class="mat-cell" colspan="4">
						<div class="ml-4">{{ t('dashboard.noDataFound') }}</div>
					</td>
				</tr>
			</table>

			@if (isPaginatedMode) {
				<ara-paginator
					[length]="totalElements()"
					[pageIndex]="currentPage()"
					[pageSize]="currentPageSize"
					[pageSizeOptions]="pageSizeOptions"
					[showFirstLastButtons]="true"
					(page)="onPageChange($event)"></ara-paginator>
			}
		</div>
	</div>
</ng-template>
